import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { PssAuthService, PssSession, PssFriend, PssPrivateMessage } from '../../../services/pss-auth.service';
import { FeatureFlagService, FEATURE_FLAGS } from '../../../services/feature-flag.service';

interface FriendThread {
  friend: PssFriend;
  messages: PssPrivateMessage[];
  latestMessage: PssPrivateMessage | null;
  isPending: boolean;
}

@Component({
  selector: 'app-friends-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe],
  templateUrl: './friends-tab.component.html',
  styleUrls: ['./friends-tab.component.css']
})
export class FriendsTabComponent implements OnInit {
  @Input() session: PssSession | null = null;

  @Output() pendingCount = new EventEmitter<number>();

  friendThreads: FriendThread[] = [];
  friendsLoading = false;
  friendsError = '';
  selectedFriendId: string | null = null;
  friendReplyText = '';
  friendReplySending = false;
  friendsDebug = '';
  friendSearchTerm = '';

  get filteredFriendThreads(): FriendThread[] {
    const q = this.friendSearchTerm.trim().toLowerCase();
    if (!q) return this.friendThreads;
    return this.friendThreads.filter(t => (t.friend.name || '').toLowerCase().includes(q));
  }

  get basicPlus(): boolean { return this.featureFlags.isBasicPlus(); }
  get restrictedMode(): boolean { return this.featureFlags.isRestrictedMode(); }

  constructor(private auth: PssAuthService, private router: Router, private featureFlags: FeatureFlagService) {}

  ngOnInit(): void {
    this.loadFriends();
  }

  loadFriends(isRetry = false): void {
    this.friendsLoading = true;
    this.friendsError = '';
    forkJoin({
      friends:  this.auth.listFriends(),
      messages: this.auth.getAllPrivateMessages().pipe(catchError(() => of([] as PssPrivateMessage[]))),
    }).subscribe({
      next: ({ friends, messages }) => {
        const byUser = new Map<string, PssPrivateMessage[]>();
        for (const msg of messages) {
          const list = byUser.get(msg.fromUserId) ?? [];
          list.push(msg);
          byUser.set(msg.fromUserId, list);
        }
        byUser.forEach(list => list.sort((a, b) => a.sentDate.localeCompare(b.sentDate)));

        const pendingIds = new Set(friends.pending.map(f => f.userId));
        this.pendingCount.emit(pendingIds.size);
        const threads: FriendThread[] = [];
        const addedIds = new Set<string>();
        const myId = this.session?.userId ?? '';

        for (const f of friends.confirmed) {
          if (f.userId === myId) continue;
          addedIds.add(f.userId);
          const msgs = byUser.get(f.userId) ?? [];
          threads.push({
            friend: f,
            messages: msgs,
            latestMessage: msgs.length ? msgs[msgs.length - 1] : null,
            isPending: false,
          });
        }

        byUser.forEach((msgs, userId) => {
          if (!userId || userId === myId || addedIds.has(userId)) return;
          const latest = msgs[msgs.length - 1];
          const synthetic: PssFriend = {
            userId,
            name: latest.fromName || userId,
            trophies: '0',
            unreadMessages: 0,
            lastInteraction: latest.sentDate,
          };
          addedIds.add(userId);
          threads.push({
            friend: synthetic,
            messages: msgs,
            latestMessage: latest,
            isPending: pendingIds.has(userId),
          });
        });

        threads.sort((a, b) => {
          // Put invite threads first
          const aInvite = a.latestMessage?.isInvite ? 1 : 0;
          const bInvite = b.latestMessage?.isInvite ? 1 : 0;
          if (aInvite !== bInvite) return bInvite - aInvite;
          if (!a.latestMessage && !b.latestMessage) return 0;
          if (!a.latestMessage) return 1;
          if (!b.latestMessage) return -1;
          return b.latestMessage.sentDate.localeCompare(a.latestMessage.sentDate);
        });

        this.friendThreads  = threads;
        this.friendsLoading = false;
      },
      error: err => {
        this.pendingCount.emit(0);
        if (!isRetry) { this.reauthAndRun(() => this.loadFriends(true)); return; }
        this.friendsError   = err.message ?? 'Could not load friends';
        this.friendsLoading = false;
      },
    });
  }

  selectFriendThread(thread: FriendThread): void {
    if (this.selectedFriendId === thread.friend.userId) {
      this.selectedFriendId = null;
      return;
    }
    this.selectedFriendId = thread.friend.userId;
    this.friendReplyText  = '';
    this.friendsDebug = `${thread.friend.name}: ${thread.messages.length} message(s)`;
  }

  get selectedFriendThread(): FriendThread | null {
    return this.friendThreads.find(t => t.friend.userId === this.selectedFriendId) ?? null;
  }

  sendFriendReply(): void {
    if (this.restrictedMode) return;
    const thread = this.selectedFriendThread;
    if (!thread || !this.friendReplyText.trim()) return;
    this.friendReplySending = true;
    this.auth.sendPrivateMessage(thread.friend.userId, this.friendReplyText.trim()).subscribe({
      next: () => {
        this.friendReplySending = false;
        this.friendReplyText    = '';
      },
      error: err => {
        this.friendReplySending = false;
        this.friendsError = err.message ?? 'Failed to send message';
      },
    });
  }

  private reauthAndRun(action: () => void): void {
    this.auth.reauthenticate().subscribe({
      next: newSession => {
        this.session = newSession;
        action();
      },
      error: () => this.router.navigate(['/'])
    });
  }
}
